'use client';

import { Button } from '../components/button';
import { Input } from '../components/input';
import { GoogleIcon } from '../icons/google-icon';

import { useAppSettings } from '@bells/core';
import { AuthCookieName } from '@bells/core/auth';
import { useLanguage } from '@bells/core/i18n';
import {
  authEmail,
  authOr,
  authPhone,
  authPlaceholder,
  authWithEmail,
  authWithGoogle,
  authWithPhone,
} from '@bells/i18n/messages';
import {
  ProfileType,
  SignInContractEmailSchema,
  SignInContractPhoneSchema,
} from '@bells/schema/models/signup';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import Cookies from 'js-cookie';
import { Mail, Phone } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/form';
import {
  FormRoot,
  FormServerError,
  SubmitButton,
} from '../components/form-root';
import { useValidationForm } from '../hooks/use-validation';

import { Skeleton } from '../components/skeleton';

import dynamic from 'next/dynamic';

const PhoneInput = dynamic(() => import('../components/phone-input'), {
  loading: () => <Skeleton className='h-[40px] w-full'></Skeleton>,
});

export type SignInType = 'phone' | 'email';

import authEmailContract from '@bells/api/auth/signin-email';
import authGoogleContract from '@bells/api/auth/signin-google';
import authPhoneContract from '@bells/api/auth/signin-phone';
import { generateClient } from '@bells/core/rest/client';

const authGoogleClient = generateClient(authGoogleContract);

export type RouteTo = (
  redirectUrl: string,
  accessToken: string,
  callbackUrl?: string
) => string;

export const defaultRouteTo: RouteTo = (
  redirectUrl: string,
  accessToken: string,
  callbackUrl?: string
) => {
  if (callbackUrl) {
    return `${redirectUrl}/${accessToken}?callbackUrl=${callbackUrl}`;
  } else {
    return `${redirectUrl}/${accessToken}`;
  }
};

export type SignInProps = {
  type?: SignInType;
  callbackUrl?: string;
  error?: string;
  routeTo?: RouteTo;
  profileType?: ProfileType;
  verifyUrl?: string;
};

const SignInComponent = ({
  type = 'email',
  callbackUrl,
  routeTo = defaultRouteTo,
  profileType = ProfileType.Customer,
  verifyUrl = '/auth/verify',
  error,
}: SignInProps) => {
  const { language } = useLanguage();
  const [signInType, setSignInType] = useState<SignInType>(type);
  const router = useRouter();

  const emailForm = useValidationForm({
    schema: SignInContractEmailSchema,
    router: authEmailContract.signInEmail,
    mode: 'onChange',
    defaultValues: { email: '', profileType, lang: language },
    onSuccess: (response, form) => {
      form.reset();
      router.push(routeTo(verifyUrl, response.body.accessToken, callbackUrl));
    },
  });

  const phoneForm = useValidationForm({
    schema: SignInContractPhoneSchema,
    router: authPhoneContract.signInPhone,
    mode: 'onChange',
    onSuccess: (response, form) => {
      form.reset();
      router.push(routeTo(verifyUrl, response.body.accessToken, callbackUrl));
    },
    defaultValues: { profileType, lang: language },
  });

  const onGoogleAuthToken = async (token: string) => {
    const result = await authGoogleClient.signInGoogle({
      body: {
        credential: token,
        profileType: ProfileType.Customer,
        lang: language,
      },
      extraHeaders: { 'x-language': language },
    });

    if (result && result.status == 200) {
      Cookies.set(AuthCookieName, result.body.accessToken);
      window.location.href = callbackUrl ?? '/';
    }
  };
  const promptGoogleSignIn = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      onGoogleAuthToken(tokenResponse.access_token);
    },
  });

  useEffect(() => {
    if (error) {
      emailForm.setError('root.server', { type: '', message: error });
    }
  }, [error]);
  return (
    <div className='grid gap-4'>
      <div className='grid gap-8'>
        {signInType == 'email' && (
          <FormRoot className='flex flex-col gap-4' form={emailForm}>
            <FormServerError></FormServerError>
            <FormField
              control={emailForm.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{authEmail()}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      type='email'
                      autoComplete='email'
                      placeholder={authPlaceholder()}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SubmitButton className='w-full'>{authWithEmail()}</SubmitButton>
          </FormRoot>
        )}

        {signInType == 'phone' && (
          <FormRoot form={phoneForm}>
            <FormField
              onlyOnTouched
              control={phoneForm.control}
              name='phone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{authPhone()}</FormLabel>
                  <FormControl>
                    <PhoneInput defaultCountry='DE' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SubmitButton className='mt-6 w-full'>
              {authWithPhone()}
            </SubmitButton>
          </FormRoot>
        )}
      </div>
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t'></span>
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-background px-2 text-muted-foreground'>
            {authOr()}
          </span>
        </div>
      </div>
      {signInType == 'phone' && (
        <Button
          onClick={() => setSignInType('email')}
          variant='outline'
          className='w-full'
        >
          <Mail className='mr-2 h-4 w-4 text-black' />
          {authWithEmail()}
        </Button>
      )}
      {signInType == 'email' && (
        <Button
          onClick={() => setSignInType('phone')}
          variant='outline'
          className='w-full'
        >
          <Phone className='mr-2 h-4 w-4 text-black' />
          {authWithPhone()}
        </Button>
      )}
      <Button
        variant='outline'
        className='w-full'
        onClick={() => promptGoogleSignIn()}
      >
        <GoogleIcon className='mr-2 h-4 w-4 text-black' />
        {authWithGoogle()}
      </Button>
    </div>
  );
};

export const SignInForm = (props: SignInProps) => {
  const { googleAuthToken } = useAppSettings();
  return (
    <GoogleOAuthProvider clientId={googleAuthToken}>
      <SignInComponent {...props}></SignInComponent>
    </GoogleOAuthProvider>
  );
};
